import { isOfficeError } from './guards'

/**
 * Formats unknown error types into Error objects for consistent error handling.
 *
 * @param error - The error value to format, can be of any type
 * @returns A properly formatted Error object
 *
 * @remarks
 * This function handles various error types:
 * - `null` or `undefined` returns a generic unknown error
 * - Error instances are returned as-is
 * - Strings are wrapped in a new Error
 * - Objects are JSON stringified (with fallback for circular references)
 * - All other types are converted to string
 *
 * @example
 * ```typescript
 * errorFormatter(null) // Error: Unknown error (null/undefined)
 * errorFormatter(new Error('test')) // Error: test
 * errorFormatter('Something went wrong') // Error: Something went wrong
 * errorFormatter({ code: 404, message: 'Not found' }) // Error: {"code":404,"message":"Not found"}
 * errorFormatter(123) // Error: 123
 * ```
 */
export const errorFormatter = (error: unknown): Error => {
  if (error == null) {
    return new Error('Unknown error (null/undefined)')
  }

  if (error instanceof Error) {
    return error
  }

  if (typeof error === 'string') {
    return new Error(error)
  }

  if (typeof error === 'object') {
    try {
      return new Error(JSON.stringify(error))
    } catch {
      return new Error('Error: [object could not be serialized]')
    }
  }

  return new Error(String(error))
}

export const errorStringify = (error: unknown): string => {
  // Check for Office error pattern before converting to Error object
  if (isOfficeError(error)) {
    return `name: ${error.name}, code: ${error.code}, message: ${error.message}`
  }

  // Ensure we have a proper Error object for non-Office errors
  const errorObj = error instanceof Error ? error : errorFormatter(error)

  // For regular errors, include more comprehensive information
  const parts = [`name: ${errorObj.name}`, `message: ${errorObj.message}`]

  // Include stack trace if available (useful for debugging)
  if (errorObj.stack) {
    parts.push(`stack: ${errorObj.stack}`)
  }

  // Include any additional error properties (like cause, if present)
  if ('cause' in errorObj && errorObj.cause) {
    parts.push(`cause: ${errorObj.cause}`)
  }

  return parts.join(', ')
}

export const handleError = (error: unknown) => errorFormatter(error)
